import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
  IconButton
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../../../theme/theme.js';
import { useNotification } from '../../../contexts/NotificationContext';

// Hooks
import { useConfirmationForm } from './hooks/useConfirmationForm';

// Components
import SignatureDialog from './components/SignatureDialog';
import DraftSaveButton from '../../../components/DraftSaveButton';
import { DRAFT_KEYS } from '../../../utils/draftUtils';

// Screens
import PatientInfoSection from './screens/PatientInfoSection';
import VitalsAndMedicationSection from './screens/VitalsAndMedicationSection';
import FastAndCovidSection from './screens/FastAndCovidSection';
import ChecklistSection from './screens/ChecklistSection';
import ApprovalSection from './screens/ApprovalSection';

const ConfirmationPhase = () => {
  const { uhid_no } = useParams();
  const navigate = useNavigate();

  const {
    formData,
    setFormData,
    errors,
    loading,
    success,
    signatureDialogOpen,
    signaturePadRef,
    handleChange,
    handleNameChange,
    handleSignatureOpen,
    handleSignatureSave,
    handleBlur,
    setSignatureDialogOpen,
    handleSubmit
  } = useConfirmationForm(uhid_no, navigate);

  const handleBack = () => {
    navigate(`/mainPage/${uhid_no}`, {
      state: { from: 'PreOperativePhase', status: 'success', step: 1 },
    });
  };

  const handleHomeNavigation = () => {
    navigate('/home');
  };

  if (loading && !formData.name) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: '#008080' }} />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ marginTop: 4, marginBottom: 4 }}>
        <Paper elevation={3} sx={{ padding: 4, borderRadius: 3, border: '1px solid #e0e0e0' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
            <Box>
              <Button
                onClick={handleBack}
                startIcon={<span>←</span>}
                sx={{ color: '#008080', fontWeight: 600, mb: 1, textTransform: 'none' }}
              >
                Back
              </Button>
              <Typography
                variant="h4"
                sx={{
                  color: '#008080',
                  fontWeight: 800,
                  lineHeight: 1.2
                }}
              >
                Confirmatory Phase (Pre-Op)<br /> Digitalization Checklist
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img src="/logo.png" alt="Logo" style={{ width: '32px', height: '32px' }} />
              <Typography variant="h6" sx={{ color: '#008080', fontWeight: 600 }}>OTMS</Typography>
            </Box>
          </Box>

          {/* Patient Info Bar */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            backgroundColor: '#f0f7f7', 
            p: 2, 
            borderRadius: 2,
            mb: 4,
            border: '1px solid #cce5e5'
          }}>
            <Typography variant="body1" sx={{ color: '#006666', fontWeight: 600 }}>
              UHID: <span style={{ color: '#333', fontWeight: 700 }}>{formData.uhid_no}</span> 
              &nbsp;&nbsp;|&nbsp;&nbsp; 
              Patient: <span style={{ color: '#333', fontWeight: 700 }}>{formData.name || '-'}</span>
            </Typography>
            <Button
              variant="contained"
              onClick={handleHomeNavigation}
              sx={{ 
                backgroundColor: '#008080', 
                '&:hover': { backgroundColor: '#006666' },
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Home
            </Button>
          </Box>

          <form onSubmit={handleSubmit}>
            <PatientInfoSection 
              formData={formData} 
              handleChange={handleChange} 
              errors={errors} 
              onBlur={handleBlur}
            />

            <VitalsAndMedicationSection 
              formData={formData} 
              handleChange={handleChange} 
              errors={errors} 
              onBlur={handleBlur}
              setFormData={setFormData}
            />

            <FastAndCovidSection 
              formData={formData} 
              handleChange={handleChange} 
              errors={errors} 
            />

            <ChecklistSection 
              formData={formData} 
              handleChange={handleChange} 
              handleNameChange={handleNameChange} 
              setFormData={setFormData}
              errors={errors}
              handleSignatureOpen={handleSignatureOpen}
              handleBlur={handleBlur}
            />

            <ApprovalSection 
              formData={formData} 
              handleChange={handleChange}
              handleNameChange={handleNameChange} 
              handleSignatureOpen={handleSignatureOpen}
              handleBlur={handleBlur}
              errors={errors}
            />

            {/* Submit Button */}
            <Box sx={{ mt: 6, display: 'flex', gap: 2, justifyContent: 'center' }}>
              <DraftSaveButton
                formKey={DRAFT_KEYS.CONFIRMATION_PHASE}
                uhidNo={uhid_no}
                formData={formData}
                onDraftLoaded={(draftData) => setFormData(draftData)}
                variant="outlined"
                size="large"
                isUpdate={success}
                sx={{ borderRadius: 3, px: 4, textTransform: 'none', fontWeight: 600 }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  backgroundColor: '#008080',
                  '&:hover': { backgroundColor: '#006666' },
                  px: 8,
                  py: 1.5,
                  borderRadius: 3,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  boxShadow: '0 4px 12px rgba(0, 128, 128, 0.3)',
                  textTransform: 'none'
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    <span>Processing...</span>
                  </Box>
                ) : (
                  success ? "Update Record" : "Submit Record"
                )}
              </Button>
            </Box>
          </form>

          {/* Dialogs */}
          <SignatureDialog
            open={signatureDialogOpen}
            onClose={() => setSignatureDialogOpen(false)}
            onSave={handleSignatureSave}
            signaturePadRef={signaturePadRef}
          />
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default ConfirmationPhase;
